// MUI
import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";

// Components
import Button from "@/Layouts/components/Buttons/Button";
import MoneyInput from "@/Layouts/components/InputComponents/MoneyInput";

// Third party
import { useQuery } from "@tanstack/react-query";

// Api
import { getMakes } from "@/Common/api/vehicles";

// Types
import { ISpiffSetupRule, ITiers } from "../../../../types/spiffSetup";
import { EActionType, INITIAL_RULE, TAction } from "../../reducer";
import { TErrors } from "../../validation";

// ICONS
import { HIGHEST_NUMBER, UNIT_INPUT_TYPE } from "@/Common/constants/InputData";
import LabeledInput from "@/Layouts/components/InputComponents/LabeledInput";
import { AddCircleRounded } from "@mui/icons-material";
import EligibilityTiers from "../EligibilityTiers";
import EligibilityRule from "./EligibilityRule";

interface ISalesContentProps {
	rules: ISpiffSetupRule[];
	tiers: ITiers[];
	pvrType: string;
	requiredStoreUnits?: number;
	requiredPvr?: number | null;
	applyTiersRetroactively: boolean;
	updateEligibilityRequirements: (updatedState: TAction) => void;
	errors: TErrors;
	franchises: string[];
}

const SalesContent = (props: ISalesContentProps) => {
	const {
		rules,
		updateEligibilityRequirements,
		errors,
		tiers,
		pvrType,
		requiredStoreUnits = 0,
		requiredPvr = null,
		applyTiersRetroactively,
		franchises,
	} = props;

	const { data: makesList, isLoading: isLoadingMakes } = useQuery({
		queryKey: ["makes"],
		queryFn: async () => getMakes(),
		staleTime: Infinity,
	});

	const handleAddNewRule = () => {
		updateEligibilityRequirements({ type: EActionType.ADD_RULE, payload: { rule: INITIAL_RULE } });
	};

	const handleChangeRequiredStoreUnits = (value: number) => {
		updateEligibilityRequirements({ type: EActionType.UPDATE_ALL, payload: { requiredStoreUnits: +value } });
	};

	const handleChangeRequiredPvr = (value: number | null) => {
		updateEligibilityRequirements({ type: EActionType.UPDATE_ALL, payload: { requiredPvr: value } });
	};

	return (
		<Box className="flex flex-col">
			{!makesList ? (
				<>
					<Box className="flex flex-row items-end justify-start gap-6 mt-4 min-w-[946px]">
						<Skeleton variant="rounded" width={150} height={32} />
						<Skeleton variant="rounded" width={150} height={32} />
						<Skeleton variant="rounded" width={110} height={32} />
						<Box className="flex flex-col gap-2">
							<Box className="flex flex-row gap-3">
								<Skeleton variant="circular" width={14} height={14} />
								<Skeleton variant="rounded" width={68} height={14} />
							</Box>
							<Skeleton variant="rounded" width={80} height={32} />
						</Box>
						<Divider className="!-ml-6 !-mr-2 !mb-4 w-2 !color-default" variant="fullWidth" />
						<Box className="flex flex-col gap-2">
							<Box className="flex flex-row gap-3">
								<Skeleton variant="circular" width={14} height={14} />
								<Skeleton variant="rounded" width={70} height={14} />
							</Box>
							<Skeleton variant="rounded" width={80} height={32} />
						</Box>
						<Skeleton variant="rounded" width={118} height={32} className="ml-3" />
						<Skeleton variant="rounded" width={18} height={18} className="!rounded-full mb-1.5 -ml-2" />
					</Box>
					<Skeleton variant="rectangular" width={120} height={30} className="mt-4 rounded-md" />
					<Box className="flex flex-row justify-between items-center my-8">
						<Skeleton variant="rounded" width={150} height={30} />
						<Box className="flex flex-row justify-end items-center gap-3">
							<Skeleton variant="rounded" width={100} height={20} />
							<Skeleton variant="rounded" className="!rounded-xl" width={45} height={24} />
						</Box>
					</Box>
					<Box className="flex flex-row gap-7">
						<Box className="flex flex-row justify-end w-40">
							<Skeleton variant="rounded" width={120} height={30} className="rounded-md" />
						</Box>
						<Box className="flex flex-row items-center">
							<Skeleton variant="circular" width={20} height={20} className="rounded-md me-3" />
							<Skeleton variant="rounded" width={110} height={30} className="rounded-md" />
							<Typography className="!text-base !text-default !font-sans !mx-1">to</Typography>
							<Skeleton variant="rounded" width={110} height={30} className="rounded-md" />
							<Skeleton variant="circular" width={20} height={20} className="rounded-md ms-3" />
						</Box>
					</Box>
					<Box className="flex flex-row mt-8">
						<Box className="!h-6 !w-[216px]">
							<Divider className="!text-default" orientation="vertical" />
						</Box>
						<Box className="flex flex-row justify-center pl-[106px]">
							<Typography className="!text-base !text-default !font-medium !font-montserrat">Spiff</Typography>
						</Box>
					</Box>
					<Box className="flex flex-row">
						<Typography className="!text-base !text-default !font-montserrat !font-medium !mt-10 !-rotate-90" alignSelf="start">
							Units
						</Typography>
						<Box className="flex flex-row justify-between items-center min-w-96 !h-11 bg-gray-row-color mt-0.5 -ms-3 p-2 pe-6 rounded-xl">
							<Box className="flex flex-row items-center">
								<Skeleton variant="rounded" width={80} height={30} className="rounded-md" />
								<Typography className="!text-base !text-default !font-sans !mx-1">to</Typography>
								<Skeleton variant="rounded" width={80} height={30} className="rounded-md" />
							</Box>
							<Box className="flex flex-row justify-end items-center">
								<Skeleton variant="rounded" width={110} height={30} className="rounded-md" />
							</Box>
						</Box>
					</Box>
					<Box className="flex flex-row justify-center gap-4 mt-3 w-full pe-16">
						<Skeleton variant="rounded" width={112} height={30} className="rounded-md" />
						<Skeleton variant="rounded" width={112} height={30} className="rounded-md" />
					</Box>
				</>
			) : (
				<>
					<Stack direction="row" spacing={3} alignItems="center" className="mt-4 mb-2">
						<LabeledInput
							label="Required store units:"
							labelClassName="regular-pt-sans-pro-narrow-label"
							input={{
								data: [
									{
										type: UNIT_INPUT_TYPE,
										decimalsEditable: false,
									},
								],
								value: requiredStoreUnits,
								defaultType: UNIT_INPUT_TYPE,
								sx: { maxWidth: "60px", minWidth: "60px", marginLeft: 1 },
								onActionUpdate: handleChangeRequiredStoreUnits,
							}}
						/>
						<MoneyInput
							label="Required PVR:"
							labelClassName="regular-pt-sans-pro-narrow-label"
							value={requiredPvr}
							min={-HIGHEST_NUMBER}
							max={HIGHEST_NUMBER}
							nullable							
							onChange={handleChangeRequiredPvr}
						/>
					</Stack>
					{rules.map((rule, index) => (
						<EligibilityRule
							key={rule._id}
							ruleIndex={index}
							rule={rule}
							updateEligibilityRequirements={updateEligibilityRequirements}
							errors={errors}
							makesList={makesList}
							isLoadingMakes={isLoadingMakes}
						/>
					))}
					<Button
						variant="contained"
						text="Add Rule"
						classes="btn-regular-add btn-small-height btn-regular-text !text-white !mt-4"
						endIcon={<AddCircleRounded />}
						onClick={handleAddNewRule}
						sx={{ height: "30px", width: "120px" }}
						disabled={!!errors?.details.length}
					/>
					<EligibilityTiers
						type="sales"
						tiers={tiers}
						updateEligibilityRequirements={updateEligibilityRequirements}
						pvrType={pvrType}
						applyTiersRetroactively={applyTiersRetroactively}
						franchises={franchises}
					/>
				</>
			)}
		</Box>
	);
};

export default SalesContent;
